// apps/roleforge-app/src/components/deliverables/OperatingModelPreview.tsx
// Plan 3b-ii Phase 7.1: Renders the Operating Model summary from audit.summary (markdown).
// Manual markdown renderer — no external deps.

import type { AuditRow } from '../../lib/api-client';

interface OperatingModelPreviewProps {
  audit: AuditRow;
}

// ─── Minimal inline-markdown renderer ──────────────────────────────────────
// Handles: # heading, ## subheading, - list item, **bold**, *italic*, paragraphs.

function renderInline(text: string): React.ReactNode {
  // Split on **bold** and *italic* markers
  const parts: React.ReactNode[] = [];
  // Regex: **bold** | *italic*
  const re = /\*\*(.+?)\*\*|\*(.+?)\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;

  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      parts.push(text.slice(last, m.index));
    }
    if (m[1] !== undefined) {
      // **bold**
      parts.push(<strong key={key++}>{m[1]}</strong>);
    } else if (m[2] !== undefined) {
      // *italic*
      parts.push(<em key={key++}>{m[2]}</em>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    parts.push(text.slice(last));
  }
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : <>{parts}</>;
}

function renderMarkdown(md: string): React.ReactNode[] {
  const lines = md.split('\n');
  const nodes: React.ReactNode[] = [];
  let listItems: React.ReactNode[] = [];
  let key = 0;

  const flushList = () => {
    if (listItems.length > 0) {
      nodes.push(
        <ul key={key++} className="list-disc pl-5 space-y-1 text-sm text-ink my-2">
          {listItems}
        </ul>,
      );
      listItems = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();

    if (line.startsWith('## ')) {
      flushList();
      nodes.push(
        <h3 key={key++} className="mt-4 mb-1 text-base font-semibold text-ink">
          {renderInline(line.slice(3))}
        </h3>,
      );
    } else if (line.startsWith('# ')) {
      flushList();
      nodes.push(
        <h2 key={key++} className="mt-6 mb-2 text-lg font-bold text-ink">
          {renderInline(line.slice(2))}
        </h2>,
      );
    } else if (line.startsWith('- ')) {
      listItems.push(
        <li key={key++} className="text-sm text-ink">
          {renderInline(line.slice(2))}
        </li>,
      );
    } else if (line.trim() === '') {
      flushList();
    } else {
      flushList();
      nodes.push(
        <p key={key++} className="text-sm text-ink my-1">
          {renderInline(line)}
        </p>,
      );
    }
  }

  flushList();
  return nodes;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function OperatingModelPreview({ audit }: OperatingModelPreviewProps) {
  const forkLabel = audit.audit_fork === 'forge' ? 'Forge' : 'Audit';
  const verticalLabel = audit.vertical
    ? audit.vertical
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : null;

  return (
    <div className="rounded-lg border border-line bg-card p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="inline-flex items-center rounded-full bg-brand-coral/10 px-2.5 py-0.5 text-xs font-medium text-coral">
          {forkLabel}
        </span>
        {verticalLabel && (
          <span className="inline-flex items-center rounded-full bg-surface px-2.5 py-0.5 text-xs font-medium text-graphite">
            {verticalLabel}
          </span>
        )}
      </div>

      {/* Body */}
      {audit.summary ? (
        <div data-testid="operating-model-body">{renderMarkdown(audit.summary)}</div>
      ) : (
        <p className="text-sm text-graphite italic" data-testid="operating-model-empty">
          The Blueprint summary will appear here once the agent completes synthesis.
        </p>
      )}
    </div>
  );
}
